// Wires the "Add lesson" modal that the academy detail page mounts
// once per module. Video tab: POST metadata + file size for JSON,
// then run tus against the returned CF upload URL. Text tab: regular
// form POST (the server's existing form path handles it). Document
// tab: placeholder until the objectstore upload pipeline lands.

import {
  tusUpload,
  formatBytes,
  throttleByIntegerPercent,
} from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-lesson-modal]";
const TAB_SELECTOR = "[data-lesson-tab]";
const TAB_BODY_SELECTOR = "[data-lesson-tab-body]";

export function initAcademyLessonModal(root = document) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireModal);
}

function wireModal(modal) {
  if (!firstBind(modal)) return;

  const moduleID = modal.dataset.moduleId;
  const courseID = modal.dataset.courseId;
  if (!moduleID || !courseID) return;

  const openButtons = document.querySelectorAll(
    `[data-lesson-modal-open="${cssEscape(moduleID)}"]`,
  );
  openButtons.forEach((btn) =>
    btn.addEventListener("click", () => openModal(modal)),
  );

  modal
    .querySelectorAll("[data-lesson-modal-close]")
    .forEach((btn) => btn.addEventListener("click", () => closeModal(modal)));
  modal
    .querySelectorAll("[data-lesson-modal-cancel]")
    .forEach((btn) => btn.addEventListener("click", () => closeModal(modal)));
  const backdrop = modal.querySelector("[data-lesson-modal-backdrop]");
  if (backdrop) backdrop.addEventListener("click", () => closeModal(modal));

  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeModal(modal);
  });

  modal
    .querySelectorAll(TAB_SELECTOR)
    .forEach((btn) =>
      btn.addEventListener("click", () =>
        activateTab(modal, btn.dataset.lessonTab),
      ),
    );

  wireVideoTab(modal, courseID);
}

function openModal(modal) {
  modal.classList.remove("hidden");
  resetModal(modal);
  const firstInput = modal.querySelector(
    "input[name=title]:not([type=hidden])",
  );
  if (firstInput) firstInput.focus();
}

function closeModal(modal) {
  modal.classList.add("hidden");
}

function resetModal(modal) {
  // Wipe form fields, hide uploading panel, reset progress, reset
  // tabs to Video. Lets the user open → cancel → open again without
  // stale state leaking between attempts.
  modal.querySelectorAll("form").forEach((form) => form.reset());

  const uploading = modal.querySelector("[data-lesson-uploading]");
  if (uploading) uploading.classList.add("hidden");
  const bar = modal.querySelector("[data-lesson-progress-bar]");
  if (bar) {
    bar.style.width = "0%";
    bar.classList.remove("bg-emerald-500");
    bar.classList.add("bg-brand-500");
  }
  const empty = modal.querySelector("[data-lesson-file-empty]");
  const selected = modal.querySelector("[data-lesson-file-selected]");
  if (empty) empty.style.opacity = "1";
  if (selected) selected.style.opacity = "0";
  const errorEl = modal.querySelector("[data-lesson-modal-error]");
  if (errorEl) {
    errorEl.textContent = "";
    errorEl.classList.add("hidden");
  }
  const submitBtn = modal.querySelector("[data-lesson-submit]");
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.classList.remove("opacity-60", "cursor-not-allowed");
  }

  activateTab(modal, "video");
}

function activateTab(modal, kind) {
  modal.querySelectorAll(TAB_SELECTOR).forEach((btn) => {
    const active = btn.dataset.lessonTab === kind;
    btn.setAttribute("aria-selected", active ? "true" : "false");
    btn.classList.toggle("bg-white", active);
    btn.classList.toggle("text-ink-900", active);
    btn.classList.toggle("shadow-sm", active);
    btn.classList.toggle("text-ink-500", !active);
    btn.classList.toggle("hover:text-ink-900", !active);
  });
  modal.querySelectorAll(TAB_BODY_SELECTOR).forEach((body) => {
    body.classList.toggle("hidden", body.dataset.lessonTabBody !== kind);
  });
}

function wireVideoTab(modal, courseID) {
  const form = modal.querySelector("[data-lesson-video-form]");
  if (!form) return;

  const fileInput = form.querySelector("[data-lesson-file-input]");
  const dropzone = form.querySelector("[data-lesson-dropzone]");
  const fileEmpty = form.querySelector("[data-lesson-file-empty]");
  const fileSelected = form.querySelector("[data-lesson-file-selected]");
  const fileNameEl = form.querySelector("[data-lesson-file-name]");
  const fileSizeEl = form.querySelector("[data-lesson-file-size]");
  const errorEl = form.querySelector("[data-lesson-modal-error]");
  const submitBtn = form.querySelector("[data-lesson-submit]");
  const uploadingEl = form.querySelector("[data-lesson-uploading]");
  const progressBar = form.querySelector("[data-lesson-progress-bar]");
  const progressMessage = form.querySelector("[data-lesson-progress-message]");
  const progressPercent = form.querySelector("[data-lesson-progress-percent]");

  if (!fileInput) return;

  fileInput.addEventListener("change", () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      if (fileEmpty) fileEmpty.style.opacity = "1";
      if (fileSelected) fileSelected.style.opacity = "0";
      return;
    }
    if (fileEmpty) fileEmpty.style.opacity = "0";
    if (fileSelected) fileSelected.style.opacity = "1";
    if (fileNameEl) fileNameEl.textContent = file.name;
    if (fileSizeEl) fileSizeEl.textContent = formatBytes(file.size);
  });

  if (dropzone) {
    dropzone.addEventListener("dragenter", () => {
      dropzone.dataset.dragging = "true";
    });
    dropzone.addEventListener("dragover", (event) => event.preventDefault());
    dropzone.addEventListener("dragleave", (event) => {
      if (!dropzone.contains(event.relatedTarget)) {
        dropzone.dataset.dragging = "false";
      }
    });
    dropzone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropzone.dataset.dragging = "false";
      const dt = event.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      showError(errorEl, "Choose a video file before submitting.");
      return;
    }

    const titleValue = (
      form.querySelector("input[name=title]")?.value || ""
    ).trim();
    if (!titleValue) {
      showError(errorEl, "Give the lesson a title before submitting.");
      return;
    }

    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value instanceof File) return;
      if (key === "video") return;
      params.append(key, value);
    });
    params.append("file_size_bytes", String(file.size));

    setSubmitting(submitBtn, true);
    hideError(errorEl);

    let payload;
    try {
      const resp = await fetch(form.action, {
        method: "POST",
        body: params,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!resp.ok) {
        showError(errorEl, await readErrorMessage(resp));
        setSubmitting(submitBtn, false);
        return;
      }
      payload = await resp.json();
    } catch (err) {
      console.error("academy.lesson.create: metadata POST failed", err);
      showError(errorEl, "Could not start the upload. Please try again.");
      setSubmitting(submitBtn, false);
      return;
    }

    if (!payload || !payload.upload_url || !payload.lesson_id) {
      showError(errorEl, "Server response was missing upload details.");
      setSubmitting(submitBtn, false);
      return;
    }

    if (uploadingEl) uploadingEl.classList.remove("hidden");
    setProgress(progressBar, progressPercent, progressMessage, 0, file.size, 0);

    // The admin console reuses this modal with its own destination; the
    // {lesson_id} placeholder lets the markup choose without forking
    // the script.
    const successTemplate =
      form.closest(ROOT_SELECTOR)?.dataset.lessonSuccessUrl;
    const lessonURL = successTemplate
      ? successTemplate.replace("{lesson_id}", payload.lesson_id)
      : `/academy/courses/${courseID}/lessons/${payload.lesson_id}`;

    tusUpload({
      file,
      uploadURL: payload.upload_url,
      onError: (err) => {
        console.error("academy.lesson.create: tus upload failed", err);
        if (progressMessage) progressMessage.textContent = "Upload paused.";
        showError(
          errorEl,
          "Upload failed. Delete this draft lesson and try again with a fresh upload.",
        );
        setSubmitting(submitBtn, false);
      },
      onProgress: throttleByIntegerPercent((uploaded, total, pct) => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          uploaded,
          total,
          pct,
        );
      }),
      onSuccess: () => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          file.size,
          file.size,
          100,
        );
        if (progressBar) {
          progressBar.classList.remove("bg-brand-500");
          progressBar.classList.add("bg-emerald-500");
        }
        if (progressMessage) {
          progressMessage.textContent =
            "Upload complete. Your video is being processed…";
        }
        window.location.assign(lessonURL);
      },
    });
  });
}

function setProgress(bar, percentEl, messageEl, uploaded, total, pct) {
  if (bar) bar.style.width = `${pct}%`;
  if (percentEl) percentEl.textContent = `${pct}%`;
  if (messageEl) {
    messageEl.textContent = `Uploading ${formatBytes(uploaded)} of ${formatBytes(total)}`;
  }
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

function setSubmitting(btn, isSubmitting) {
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.classList.toggle("opacity-60", isSubmitting);
  btn.classList.toggle("cursor-not-allowed", isSubmitting);
}

async function readErrorMessage(resp) {
  try {
    const body = await resp.json();
    if (body && body.error) return body.error;
  } catch (_) {
    // Fall through.
  }
  return `Request failed (${resp.status}).`;
}

// cssEscape mirrors CSS.escape (not available in older browsers). We
// only need to handle UUIDs as the value, but the dash + the v4
// hex-letter range still need quoting for selector use.
function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(value);
  }
  return String(value).replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
